import { Home, FileText, Search, Shield, CheckCircle, AlertTriangle, DollarSign, Handshake } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const BuyingSelling = () => {
  const mainContent = (
    <>
      {/* Overview Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Residential Real Estate Transactions in Houston, Texas</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Buying or selling a home is one of the most significant financial decisions you'll make. Our experienced real estate attorneys provide comprehensive legal representation throughout the entire transaction process, protecting your interests and ensuring a smooth closing. Whether you're a first-time homebuyer, seasoned investor, or selling your property, we guide you through every legal aspect of the transaction.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                Why You Need a Real Estate Attorney
              </p>
              <p className="text-muted-foreground">
                In Texas, hiring a real estate attorney isn't required by law, but it's one of the smartest investments you can make. Real estate contracts contain complex legal language, and a single overlooked clause can cost you thousands of dollars or result in years of legal disputes. Our attorneys review every document, negotiate favorable terms, and ensure you understand exactly what you're signing.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">Our Comprehensive Services for Home Buyers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Purchase Agreement Review</h3>
                <p className="text-muted-foreground mb-4">
                  We meticulously review your purchase agreement before you sign, identifying potential issues and negotiating favorable terms.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Analysis of all contract terms and conditions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Review of contingencies and deadlines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Financing and inspection clause examination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Earnest money protection strategies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Seller disclosure document review</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Search className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Title Examination & Insurance</h3>
                <p className="text-muted-foreground mb-4">
                  We conduct comprehensive title searches and coordinate title insurance to protect you from ownership disputes and hidden defects.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Complete chain of title verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Lien and judgment search</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Easement and restriction review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Title insurance policy procurement</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Resolution of title defects before closing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Shield className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Due Diligence Support</h3>
                <p className="text-muted-foreground mb-4">
                  We guide you through the inspection period and help you negotiate repairs or credits based on inspection findings.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Inspection report analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Negotiation of repair credits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>HOA document review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Survey review and boundary verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Zoning and permit verification</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Handshake className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Closing Representation</h3>
                <p className="text-muted-foreground mb-4">
                  We attend your closing to ensure all documents are properly executed and your transaction closes successfully.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Review of closing disclosure (CD)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Verification of loan documents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Final walkthrough coordination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Deed and title transfer oversight</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Post-closing document recording</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-8">Services for Home Sellers</h3>
            <div className="bg-muted/30 p-8 rounded-lg mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-accent" />
                    Listing and Contracts
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Review and negotiation of listing agreements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Analysis of offers and counteroffers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Seller disclosure preparation and compliance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Contract addendum drafting</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-accent" />
                    Title and Closing
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Resolution of title issues and liens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Negotiation of repair requests</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Review of settlement statements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Closing coordination and document execution</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Home Buying Process */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">The Home Buying Process in Texas</h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Pre-Approval & Home Search",
                  desc: "Obtain mortgage pre-approval and begin searching for your ideal property. We can review your pre-approval letter and loan estimate."
                },
                {
                  step: "2",
                  title: "Make an Offer",
                  desc: "Submit a written offer with earnest money. We review the purchase agreement and negotiate terms on your behalf before you sign."
                },
                {
                  step: "3",
                  title: "Option Period (7-10 days)",
                  desc: "Conduct inspections, review HOA documents, and perform due diligence. We help you interpret findings and negotiate repairs or credits."
                },
                {
                  step: "4",
                  title: "Title Search & Insurance",
                  desc: "Title company examines property history. We review the title commitment and resolve any defects discovered."
                },
                {
                  step: "5",
                  title: "Final Loan Approval",
                  desc: "Lender completes underwriting and issues clear to close. We review your Closing Disclosure for accuracy."
                },
                {
                  step: "6",
                  title: "Final Walkthrough",
                  desc: "Inspect the property 24-48 hours before closing to ensure agreed-upon repairs are completed and property is in agreed condition."
                },
                {
                  step: "7",
                  title: "Closing Day",
                  desc: "Sign all documents, wire funds, receive keys. We attend closing to ensure proper execution of all legal documents."
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Common Issues We Help Resolve</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Contract Disputes", desc: "Breach of contract, misrepresentation, or failure to disclose material defects" },
                { title: "Title Problems", desc: "Liens, easements, boundary disputes, or clouds on title" },
                { title: "Financing Issues", desc: "Loan denial, appraisal problems, or lender delays" },
                { title: "Inspection Disputes", desc: "Major defects, repair negotiations, or seller refusal to remedy issues" },
                { title: "HOA Issues", desc: "Undisclosed restrictions, special assessments, or approval problems" },
                { title: "Earnest Money Disputes", desc: "Contract termination issues or disagreements over earnest money release" }
              ].map((issue, index) => (
                <div key={index} className="bg-card p-6 rounded-lg border border-accent/20">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-accent" />
                    {issue.title}
                  </h3>
                  <p className="text-muted-foreground">{issue.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Choose Our Real Estate Services?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Shield className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">20+ Years Experience</h3>
                <p className="text-muted-foreground">Extensive experience in Texas real estate law and local market knowledge</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <FileText className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Thorough Review</h3>
                <p className="text-muted-foreground">Meticulous examination of every document to protect your investment</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Handshake className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Personal Service</h3>
                <p className="text-muted-foreground">Direct attorney involvement from contract to closing</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Buying & Selling Residential Property"
      subtitle="Comprehensive Legal Representation for Houston Home Buyers and Sellers"
      icon={<Home className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default BuyingSelling;